import { useCallback, useRef, useState } from 'react'

import { Alert, Box, Text, TogglerCheckbox } from '@factorialco/deprecated-design-system'
import { F0Checkbox, F0Dialog, F0Select } from '@factorialco/f0-react'
import { RichTextEditorHandle } from '@factorialco/f0-react/dist/experimental'
import moment from 'moment'
import { Features } from 'generated/featureDefinitions'
import {
  AtsCandidateEmailActionDelaysEnum,
  RejectApplicationsMutationVariables,
  RejectableApplicationFragment,
} from 'generated/resources/graphql'

import InformationTooltip from 'modules/core/components/Inputs/InformationTooltip'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useFeatureAvailability } from 'modules/core/hooks/useFeatureAvailability'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { dateToString } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { DelayMessageSelector } from 'modules/ats/components/MessageBoard/NewMessage/DelayMessageSelector'
import { ScheduleMessageModal } from 'modules/ats/components/ScheduleMessageModal'
import { TextEditorWithMessageTemplates } from 'modules/ats/components/TextEditorWithMessageTemplates'
import { useRejectApplications } from 'modules/ats/hooks/applications/useRejectApplications'
import { useCandidateEmailActions } from 'modules/ats/hooks/candidateEmailActions/useCandidateEmailActions'
import { useMessageTemplatesFeatureAvailability } from 'modules/ats/hooks/features/useMessageTemplatesFeatureAvailability'
import { useRejectionReasons } from 'modules/ats/hooks/rejectionReasons/useRejectionReasons'
import { getDelayTime, sendAsCorporateEmail } from 'modules/ats/lib/utils/candidateEmailActions'

import { RejectSmartActionPreview } from './RejectSmartActionPreview'

interface Props {
  jobPostingId: string
  selectedApplications: RejectableApplicationFragment[]
  title: string
  saveToTalentPoolCheckboxVisible: boolean
  saveToTalentPoolCheckboxValue: boolean
  onClose: () => void
  onSuccess: () => void
}

export const RejectApplicationsModal = ({
  selectedApplications,
  title,
  jobPostingId,
  onClose,
  onSuccess,
  saveToTalentPoolCheckboxVisible,
  saveToTalentPoolCheckboxValue,
}: Props) => {
  const richTextEditorRef = useRef<RichTextEditorHandle>(null)
  const { loading: rejecting, rejectApplications } = useRejectApplications()
  const hasMessageTemplatesFeature = useMessageTemplatesFeatureAvailability()
  const { reasons, loading } = useRejectionReasons()
  const applicationPhaseId = selectedApplications[0]?.applicationPhase.id
  const { candidateEmailActions, loading: loadingActions } = useCandidateEmailActions({
    atsJobPostingId: jobPostingId,
    enabled: true,
    actionType: 'reject',
    atsApplicationPhaseIds: applicationPhaseId ? [applicationPhaseId] : [],
  })
  const { featureAvailability } = useFeatureAvailability(Features.ats_smart_actions)
  const smartActionsActive = featureAvailability.available
  const action = candidateEmailActions ? candidateEmailActions[0] : undefined

  const applicationPhase = action?.atsApplicationPhases.find(
    (phase) => phase.id === applicationPhaseId
  )
  const populateAction = smartActionsActive && !!action

  const getMessageDelay = (delay?: AtsCandidateEmailActionDelaysEnum | string | null) => {
    if (!delay || delay === 'now') return null

    return getDelayTime(delay)
  }

  const form = useForm<RejectApplicationsMutationVariables>({
    defaultValues: {
      ids: [],
      atsRejectionReasonId: '-1',
      disqualifiedMessage: populateAction ? action?.messageContent : '',
      sendAsCorporateEmail: populateAction ? sendAsCorporateEmail(action.emailFrom) : true,
      talentPool: saveToTalentPoolCheckboxValue,
      messageDelayedUntil: populateAction ? action.delay : null,
      notify: false,
    },
    validate(values) {
      const errors: FormErrors<RejectApplicationsMutationVariables> = {}

      if (values.atsRejectionReasonId === '-1') {
        errors.disqualifiedMessage = i18n.t('common.validations.empty')
      }

      return errors
    },
    onSubmit: async (data, { setErrors }) => {
      const result = await rejectApplications({
        ids: selectedApplicationIds,
        disqualifiedMessage: data.disqualifiedMessage,
        attachments: attachments,
        sendAsCorporateEmail: data.sendAsCorporateEmail,
        notify: data.notify,
        talentPool: data.talentPool,
        messageDelayedUntil: getMessageDelay(data.messageDelayedUntil),
        atsRejectionReasonId: data.atsRejectionReasonId,
      })

      const handled = handleResult(result, (errors) => {
        setErrors(errors)
      })

      if (handled.success) {
        handleTracking()
        onSuccess()

        app.setFlash({
          type: 'correct',
          message: i18n.t('ats.candidates.applications.disqualify.modal.success_message'),
        })
      }

      return handled
    },
  })

  useEffect(() => {
    form.setFieldValue('talentPool', saveToTalentPoolCheckboxValue)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveToTalentPoolCheckboxValue])

  useEffect(() => {
    if (populateAction) {
      form.setFieldValue('disqualifiedMessage', action?.messageContent)
      form.setFieldValue('sendAsCorporateEmail', sendAsCorporateEmail(action.emailFrom))
      form.setFieldValue('messageDelayedUntil', action.delay)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingActions])

  const submitAndNotify = async () => {
    await form.setFieldValue('notify', true)
    form.submitForm()
  }

  const sendDelayed = (when: moment.Moment) => {
    form.setFieldValue('messageDelayedUntil', dateToString(when, 'backend_datetime'))
    submitAndNotify()
  }

  const handleCustomSchedule = (scheduledDate: string) => {
    form.setFieldValue('messageDelayedUntil', scheduledDate)
    submitAndNotify()
  }

  const selectedApplicationIds = selectedApplications.map((application) => application.id)

  const someApplicationsHasNoConsentToTalentPool = selectedApplications.some(
    (application) => !application.candidate?.consentToTalentPool
  )

  const applicationsWithoutEmail = selectedApplications.filter(
    (application) => !application.candidate?.email
  ).length

  const hasApplicationWithEmail = applicationsWithoutEmail !== selectedApplications.length
  const canSendCustomMessage = hasMessageTemplatesFeature && hasApplicationWithEmail
  const [editMessage, setEditMessage] = useState(false)
  const [showScheduleModal, setShowScheduleModal] = useState(false)
  const [isReasonSelectOpen, setIsReasonSelectOpen] = useState(false)
  const [isMessageTemplateSelectOpen, setIsMessageTemplateSelectOpen] = useState(false)
  const [isDelayMessageInvalid, setIsDelayMessageInvalid] = useState(false)
  const [attachments, setAttachments] = useState<File[]>([])
  const [reasonSelectPortalContainer, setReasonSelectPortalContainer] =
    useState<HTMLDivElement | null>(null)
  const { company } = useCurrentSession()

  const handleTracking = () => {
    if (!smartActionsActive) return

    if (editMessage) {
      track(trackingEvents.ats.smartActionEdit, {
        company_id: company.id,
        stage_id: [applicationPhase?.hiringStage?.id],
        stage_name: [applicationPhase?.hiringStage?.name || 'Unassigned'],
        job_position: jobPostingId,
        action_type: 'reject',
      })
    } else {
      track(trackingEvents.ats.smartActionPerform, {
        company_id: company.id,
        stage_id: [applicationPhase?.hiringStage?.id],
        stage_name: [applicationPhase?.hiringStage?.name || 'Unassigned'],
        job_position: jobPostingId,
        action_type: 'reject',
      })
    }
  }

  const reasonSelectPortalRef = useCallback((node: HTMLDivElement | null) => {
    setReasonSelectPortalContainer(node)
  }, [])

  if (!selectedApplications.length) return null

  const information = () => {
    if (selectedApplications?.length === 1) {
      const candidateName = selectedApplications[0]?.candidate?.fullName
      const params = {
        candidate_name: candidateName,
      }

      return someApplicationsHasNoConsentToTalentPool
        ? i18n.t(
            'ats.candidates.applications.disqualify.modal.talent_pool_negative_consent',
            params
          )
        : i18n.t(
            'ats.candidates.applications.disqualify.modal.talent_pool_affirmative_consent',
            params
          )
    } else {
      return someApplicationsHasNoConsentToTalentPool
        ? i18n.t('ats.candidates.applications.disqualify.modal.bulk_consent_negative')
        : i18n.t('ats.candidates.applications.disqualify.modal.bulk_consent_affirmative')
    }
  }

  const getDelayPrimaryActions = () => {
    const isDisabled = rejecting || !form.isValid
    return [
      {
        value: 'send-now',
        label: i18n.t('ats.candidates.messages.delayed_messages.send_now'),
        onClick: submitAndNotify,
        disabled: isDisabled,
      },
      {
        value: 'send-2h',
        label: i18n.t('ats.candidates.messages.delayed_messages.send_later_2_hours'),
        onClick: () => sendDelayed(moment().add(2, 'hours')),
        disabled: isDisabled,
      },
      {
        value: 'send-4h',
        label: i18n.t('ats.candidates.messages.delayed_messages.send_later_4_hours'),
        onClick: () => sendDelayed(moment().add(4, 'hours')),
        disabled: isDisabled,
      },
      {
        value: 'send-6h',
        label: i18n.t('ats.candidates.messages.delayed_messages.send_later_6_hours'),
        onClick: () => sendDelayed(moment().add(6, 'hours')),
        disabled: isDisabled,
      },
      ...(moment().isBefore(moment().startOf('day').hour(18))
        ? [
            {
              value: 'send-today-6pm',
              label: i18n.t('ats.candidates.messages.delayed_messages.send_today_6_pm'),
              onClick: () => sendDelayed(moment().startOf('day').hour(18)),
              disabled: isDisabled,
            },
          ]
        : []),
      {
        value: 'send-tomorrow',
        label: i18n.t('ats.candidates.messages.delayed_messages.send_tomorrow'),
        onClick: () => sendDelayed(moment().add(1, 'day').startOf('day').hour(9)),
        disabled: isDisabled,
      },
      {
        value: 'send-next-monday',
        label: i18n.t('ats.candidates.messages.delayed_messages.send_next_monday'),
        onClick: () => sendDelayed(moment().day(8).startOf('day').hour(9)),
        disabled: isDisabled,
      },
      {
        value: 'schedule',
        label: i18n.t('ats.candidates.messages.delayed_messages.schedule'),
        onClick: () => setShowScheduleModal(true),
        disabled: isDisabled,
      },
    ]
  }

  const handleDialogClose = () => {
    // Close floating select layers first, then unmount dialog.
    setIsReasonSelectOpen(false)
    setIsMessageTemplateSelectOpen(false)
    setTimeout(onClose, 0)
  }

  const onFilesChange = (files: File[] | null | undefined) => {
    setAttachments(files ?? [])
  }

  return (
    <>
      <F0Dialog
        isOpen
        title={title}
        description={i18n.tp('ats.candidates.applications.disqualify.modal.description', {
          count: selectedApplicationIds.length,
        })}
        onClose={handleDialogClose}
        primaryAction={
          !canSendCustomMessage
            ? {
                label: i18n.t('ats.candidates.applications.disqualify.modal.disqualify'),
                onClick: form.submitForm,
                disabled: rejecting || !form.isValid,
              }
            : smartActionsActive
              ? {
                  label: i18n.t('ats.candidates.applications.disqualify.modal.disqualify'),
                  onClick: submitAndNotify,
                  disabled:
                    rejecting ||
                    !form.isValid ||
                    !form.values.disqualifiedMessage ||
                    isDelayMessageInvalid,
                }
              : getDelayPrimaryActions()
        }
        secondaryAction={
          canSendCustomMessage
            ? {
                label: i18n.t('ats.candidates.applications.disqualify.modal.dont_sent_message'),
                onClick: form.submitForm,
                disabled: rejecting || form.values.atsRejectionReasonId === '-1',
              }
            : undefined
        }
      >
        <Box gap="s24">
          <Box gap="s4">
            <Box flexDirection="row" gap="s4" alignItems="center">
              <Text.Normal size="200" weight="medium" color="grey1000">
                {i18n.t('ats.candidates.applications.disqualify.modal.reason_to_disqualify')}
              </Text.Normal>
              <InformationTooltip
                information={i18n.t(
                  'ats.candidates.applications.disqualify.modal.rejection_information'
                )}
                icon="question"
              />
            </Box>
            <F0Select
              options={reasons.map((reason) => ({
                label: reason.reason,
                value: String(reason.id),
              }))}
              value={
                form.values.atsRejectionReasonId !== '-1'
                  ? form.values.atsRejectionReasonId
                  : undefined
              }
              onChange={(value: string) => form.setFieldValue('atsRejectionReasonId', value)}
              searchEmptyMessage={i18n.t(
                'ats.candidates.applications.disqualify.modal.no_search_match'
              )}
              label={i18n.t('ats.candidates.applications.disqualify.modal.reason')}
              hideLabel
              showSearchBox
              searchBoxPlaceholder={i18n.t('common.search_by')}
              placeholder={i18n.t(
                'ats.candidates.applications.disqualify.modal.select_placeholder'
              )}
              disabled={loading}
              portalContainer={reasonSelectPortalContainer}
              open={isReasonSelectOpen}
              onOpenChange={setIsReasonSelectOpen}
            />
          </Box>
          {saveToTalentPoolCheckboxVisible && (
            <Box flexDirection="row" gap="s4" alignItems="center">
              <F0Checkbox
                checked={form.values.talentPool ?? false}
                onCheckedChange={(checked) => form.setFieldValue('talentPool', checked)}
                title={i18n.t(
                  'ats.candidates.applications.disqualify.modal.save_candidate_talent_pool'
                )}
              />
              <InformationTooltip information={information()} />
            </Box>
          )}
          {canSendCustomMessage &&
            !loadingActions &&
            (populateAction && !editMessage ? (
              <RejectSmartActionPreview
                candidateEmailAction={action}
                onEdit={() => setEditMessage(!editMessage)}
              />
            ) : (
              <Box gap="s16">
                <TextEditorWithMessageTemplates
                  richTextEditorRef={richTextEditorRef}
                  onChange={(value) => form.setFieldValue('disqualifiedMessage', value)}
                  onFiles={onFilesChange}
                  placeholder={i18n.t('ats.candidates.applications.disqualify.modal.placeholder')}
                  value={form.values.disqualifiedMessage || ''}
                  showLabel
                  messageTemplateSelectOpen={isMessageTemplateSelectOpen}
                  onMessageTemplateSelectOpenChange={setIsMessageTemplateSelectOpen}
                />

                <TogglerCheckbox
                  value={form.values.sendAsCorporateEmail ?? false}
                  onChange={(sendAsCorporateEmailValue) =>
                    form.setFieldValue('sendAsCorporateEmail', sendAsCorporateEmailValue)
                  }
                  label={i18n.t('ats.candidates.applications.disqualify.modal.send_as_corporate')}
                  information={i18n.t(
                    'ats.candidates.applications.disqualify.modal.send_as_corporate_tooltip'
                  )}
                />
                {smartActionsActive && (
                  <DelayMessageSelector
                    label={i18n.t('ats.smart_actions.sidepanel.delay')}
                    value={form.values.messageDelayedUntil || ''}
                    onChange={(messageDelayedUntil) =>
                      form.setFieldValue('messageDelayedUntil', messageDelayedUntil)
                    }
                    inlineCustomPicker
                    onValidationChange={setIsDelayMessageInvalid}
                  />
                )}
              </Box>
            ))}
          {applicationsWithoutEmail > 0 ? (
            <Alert color="warning">
              {i18n.t('ats.candidates.applications.bulk_disqualify.applications_without_email', {
                count: applicationsWithoutEmail,
              })}
            </Alert>
          ) : null}
        </Box>
        <Box ref={reasonSelectPortalRef} style={{ position: 'relative', zIndex: '999999' }} />
      </F0Dialog>
      {canSendCustomMessage && !smartActionsActive && (
        <ScheduleMessageModal
          isOpen={showScheduleModal}
          onClose={() => setShowScheduleModal(false)}
          onConfirm={handleCustomSchedule}
        />
      )}
    </>
  )
}
